import { addMonths } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../common/util/date-util'
import { formatHour, timeSpanToTickDistanceMap } from './chart-util'
import { TimeResolution } from './ChartTimeResolutionSelect'
import { TimeSpan } from './ChartTimeSpanSelect'

type UseChartFormatters = {
  getXAxisValueFormatter: (
    resolution: TimeResolution,
  ) => (value: number) => string
  getTimeFormatter: (resolution: TimeResolution) => (value: number) => string
}

/**
 * Due to performance reasons with 8760 ticks, we cannot handle data points
 * with dates and we need to use the hour index instead. Thus we add some
 * hacks to find out when to print the legend based on the hour index.
 * */
export const useTimeChartFormatters = (
  timeSpan: TimeSpan,
  selectedDate: Date,
): UseChartFormatters => {
  const { t } = useTranslation()

  const tickDistance = timeSpanToTickDistanceMap[timeSpan]

  const hourlyXAxisValueFormatter = (hour: number) =>
    (hour - 1) % tickDistance === 0
      ? formatHour(
          selectedDate,
          hour,
          t(`date.axisFormatByTimeSpan.${timeSpan}`),
        )
      : ''

  const monthlyXAxisValueFormatter = (month: number) =>
    formatDate(
      addMonths(selectedDate, month),
      t(`date.axisFormatByTimeSpan.${timeSpan}`),
    )

  const getXAxisValueFormatter = (resolution: TimeResolution) =>
    resolution === TimeResolution.Hour
      ? hourlyXAxisValueFormatter
      : monthlyXAxisValueFormatter

  const getTimeFormatter = (resolution: TimeResolution) => (time: number) =>
    resolution === TimeResolution.Month
      ? formatDate(
          addMonths(selectedDate, time),
          t(`date.axisFormatByTimeSpan.${TimeSpan.Month}`),
        )
      : formatHour(selectedDate, time, t('date.short'))
  return { getXAxisValueFormatter, getTimeFormatter }
}
